import { adoptSession, loginAndBoot } from "../persistence";
import {
  requestPasswordReset,
  resetPassword,
  signup,
  fetchSignupToken,
  type SignupFormToken,
  verifyEmail,
  type AuthActionResult
} from "../../auth";
import type { ScreenContext } from "../app";

/**
 * Sign in, sign up, and the two flows that redeem a token from a mailed link
 * (design doc 16.90).
 *
 * Why the token flows live here rather than at their own URLs: a verification
 * or reset mail links to the GAME with a token in the query string, and this
 * screen posts it to the matching endpoint. The indirection is the security
 * control -- a mail scanner or link-preview bot prefetching the link performs a
 * GET against the game and consumes nothing, where a link straight to an
 * endpoint would burn a single-use token before the recipient ever clicked.
 */
type Mode = "login" | "signup" | "forgot" | "verifying" | "reset";

export function renderLoginScreen(container: HTMLElement, ctx: ScreenContext): void {
  const wrap = document.createElement("div");
  wrap.className = "login-wrap";
  container.appendChild(wrap);

  // A token in the URL decides the mode, overriding the default. Read once at
  // entry so a rerender inside a flow does not restart it.
  const params = new URLSearchParams(window.location.search);
  const verifyToken = params.get("verify");
  const resetToken = params.get("reset");

  let mode: Mode = verifyToken ? "verifying" : resetToken ? "reset" : "login";
  paint();

  /**
   * Removes the token from the address bar without reloading.
   *
   * Worth doing rather than leaving it: a single-use token sitting in the URL
   * survives into browser history and into any Referer sent to a third party,
   * and a stale one produces a baffling "that link is not valid" if the player
   * later refreshes the page.
   */
  function clearTokenFromUrl(): void {
    const url = new URL(window.location.href);
    url.searchParams.delete("verify");
    url.searchParams.delete("reset");
    window.history.replaceState({}, "", url.toString());
  }

  function paint(): void {
    wrap.innerHTML = "";
    const h1 = document.createElement("h1");
    h1.textContent = "Starforge";
    wrap.appendChild(h1);

    if (mode === "login") paintLogin();
    else if (mode === "signup") paintSignup();
    else if (mode === "forgot") paintForgot();
    else if (mode === "verifying") paintVerifying();
    else paintReset();
  }

  // -------------------------------------------------------------------------
  // Small builders, so each mode reads as its own form rather than one big
  // template with half its fields hidden.
  // -------------------------------------------------------------------------

  function form(html: string): {
    el: HTMLFormElement;
    error: HTMLElement;
    note: HTMLElement;
    button: HTMLButtonElement;
  } {
    const f = document.createElement("form");
    f.className = "login-form";
    f.innerHTML = `${html}
      <p class="form-error" data-error style="display:none"></p>
      <p class="dim" data-note style="display:none"></p>`;
    wrap.appendChild(f);
    return {
      el: f,
      error: f.querySelector<HTMLElement>("[data-error]")!,
      note: f.querySelector<HTMLElement>("[data-note]")!,
      button: f.querySelector<HTMLButtonElement>('button[type="submit"]')!
    };
  }

  function link(text: string, to: Mode): HTMLButtonElement {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "link-button";
    b.textContent = text;
    b.addEventListener("click", () => {
      mode = to;
      paint();
    });
    return b;
  }

  function footer(...nodes: Node[]): void {
    const p = document.createElement("p");
    p.className = "dim login-alt";
    nodes.forEach((n, i) => {
      if (i > 0) p.appendChild(document.createTextNode(" · "));
      p.appendChild(n);
    });
    wrap.appendChild(p);
  }

  function show(el: HTMLElement, text: string): void {
    el.textContent = text;
    el.style.display = "block";
  }

  /** Disables the button while a request is in flight, restoring the label after. */
  function busy(button: HTMLButtonElement, label: string): () => void {
    const original = button.textContent ?? "";
    button.disabled = true;
    button.textContent = label;
    return () => {
      button.disabled = false;
      button.textContent = original;
    };
  }

  // -------------------------------------------------------------------------
  // Sign in
  // -------------------------------------------------------------------------

  function paintLogin(): void {
    const sub = document.createElement("p");
    sub.className = "dim";
    sub.textContent = "Sign in to continue.";
    wrap.appendChild(sub);

    const { el, error, note, button } = form(`
      <label>
        Email
        <input type="text" name="username" autocomplete="username" required />
      </label>
      <label>
        Password
        <input type="password" name="password" autocomplete="current-password" required />
      </label>
      <button type="submit">Log In</button>
    `);

    // Not type="email": existing accounts predating 16.90 sign in with a plain
    // username, and the server accepts either. A stricter input would lock
    // those players out of their own game.
    el.addEventListener("submit", (e) => {
      e.preventDefault();
      const data = new FormData(el);
      const username = String(data.get("username") ?? "").trim();
      const password = String(data.get("password") ?? "");
      if (!username || !password) return;

      const done = busy(button, "Logging in...");
      error.style.display = "none";
      note.style.display = "none";

      loginAndBoot(username, password).then((result) => {
        if (result.success) {
          ctx.rerender();
          return;
        }
        show(error, result.error ?? "Login failed.");
        if (result.unverified) {
          show(note, "Not seeing the email? Use “Forgot password” — that link confirms your address too.");
        }
        done();
      });
    });

    googleButton();
    footer(link("Create an account", "signup"), link("Forgot password", "forgot"));
  }

  // -------------------------------------------------------------------------
  // Sign up
  // -------------------------------------------------------------------------

  /**
   * The animated shield beside a new-password field, and the advice it opens.
   *
   * Deliberately duplicated in shape (not in text) with the shared account
   * page's helper: the wording lives in `auth-client.js` as
   * `DrsAuth.PASSWORD_ADVICE_HTML` so both surfaces say the identical thing,
   * because two hand-maintained copies of security advice is how one of them
   * ends up out of date and wrong.
   *
   * Attached only where a password is CHOSEN. Never on the sign-in field: the
   * password already exists there, and advice about picking one is noise.
   */
  /**
   * A real link to Google sign-in, an ADDITIONAL method alongside the embedded
   * form above it, never a replacement (auth/CLAUDE.md).
   *
   * A plain <a>, not a fetch() call bound to a click handler: this has to be a
   * genuine top-level navigation, because the browser must actually leave this
   * page for accounts.google.com and no amount of JavaScript can do that from
   * inside a fetch. The callback that handles Google's answer lives in Drug
   * Wars' ASP.NET Core application, not here or in the shared auth service --
   * validating an ID token against Google's rotating JWKS keys needs a real
   * JWT story neither this game nor the .NET Framework 4.8 auth service has.
   * This screen never learns any of that happened; it just gets the same
   * drs_auth cookie back that a password login sets, and a page reload is
   * enough to pick it up via the app's normal session check on boot.
   *
   * The return destination is wherever this page already is -- reloading it
   * is what re-runs that boot check.
   */
  function googleButton(): void {
    const divider = document.createElement("p");
    divider.className = "dim login-alt";
    divider.textContent = "or";
    wrap.appendChild(divider);

    const a = document.createElement("a");
    a.className = "btn-google";
    a.href = "https://aigames.win/drugwars/auth/google?returnUrl="
      + encodeURIComponent(window.location.origin + window.location.pathname);
    a.innerHTML =
      '<svg viewBox="0 0 48 48" aria-hidden="true">' +
      '<path fill="#EA4335" d="M24 9.5c3.4 0 6.4 1.2 8.8 3.5l6.5-6.5C35.2 2.6 30 0 24 0 14.6 0 6.4 5.4 2.5 13.2l7.6 5.9C12 12.6 17.5 9.5 24 9.5z"/>' +
      '<path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.2-.4-4.7H24v9h12.6c-.5 3-2.2 5.4-4.7 7.1l7.3 5.7C43.5 37.5 46.5 31.5 46.5 24.5z"/>' +
      '<path fill="#FBBC05" d="M10.1 19.1a14.4 14.4 0 0 0 0 9.8l-7.6 5.9a24 24 0 0 1 0-21.6z"/>' +
      '<path fill="#34A853" d="M24 48c6.5 0 12-2.1 16-5.8l-7.3-5.7c-2 1.4-4.7 2.2-8.7 2.2-6.5 0-12-4.4-13.9-10.4l-7.6 5.9C6.4 42.6 14.6 48 24 48z"/>' +
      "</svg>Continue with Google";
    wrap.appendChild(a);
  }

  function attachPasswordHelper(form: HTMLFormElement): void {
    const input = form.querySelector<HTMLInputElement>('input[autocomplete="new-password"]');
    if (!input) return;

    const row = document.createElement("div");
    row.className = "pw-row";
    input.parentNode?.insertBefore(row, input);
    row.appendChild(input);

    const button = document.createElement("button");
    button.type = "button"; // never submits
    button.className = "pw-help";
    button.setAttribute("aria-label", "Advice on choosing a password");
    button.setAttribute("aria-expanded", "false");
    button.innerHTML =
      '<svg viewBox="0 0 24 24" aria-hidden="true">' +
      '<path class="shield" d="M12 2 4 5v6c0 5 3.4 9.4 8 11 4.6-1.6 8-6 8-11V5l-8-3z"/>' +
      '<path d="M12 7a2.4 2.4 0 0 0-2.4 2.4v1.1h-.4v4.4h5.6v-4.4h-.4V9.4A2.4 2.4 0 0 0 12 7zm0 1.4c.6 0 1 .5 1 1v1.1h-2V9.4c0-.5.4-1 1-1z" fill="#10131a"/>' +
      "</svg>";
    row.appendChild(button);

    const note = document.createElement("div");
    note.className = "pw-note";
    note.id = "pw-note-" + Math.random().toString(36).slice(2, 8);
    button.setAttribute("aria-controls", note.id);
    note.innerHTML = window.DrsAuth.PASSWORD_ADVICE_HTML;
    row.parentNode?.insertBefore(note, row.nextSibling);

    button.addEventListener("click", () => {
      const open = note.classList.toggle("open");
      button.setAttribute("aria-expanded", open ? "true" : "false");
    });
  }

  function paintSignup(): void {
    const sub = document.createElement("p");
    sub.className = "dim";
    sub.textContent = "Create an account. We'll email you a link to confirm the address.";
    wrap.appendChild(sub);

    /*
     * The anti-bot guard's token (auth A-16), fetched NOW rather than at submit
     * time. The server measures how long the form was on screen, so a token
     * minted when the button is pressed is refused as too fast -- the one
     * mistake this mechanism makes easy.
     *
     * Held in a local because it belongs to this painting of the form: leaving
     * the screen and coming back repaints, and a fresh form deserves a fresh
     * token rather than one whose clock started in a session the player has
     * already abandoned.
     */
    let guard: SignupFormToken | null = null;
    void fetchSignupToken().then((t) => { guard = t; });

    const { el, error, note, button } = form(`
      <label>
        Email
        <input type="email" name="email" autocomplete="email" required />
      </label>
      <label>
        Password
        <input type="password" name="password" autocomplete="new-password" minlength="10" required />
      </label>
      <button type="submit">Create Account</button>
      <!--
        Honeypot (auth A-16). Off-screen rather than display:none, because some
        bots deliberately skip fields that are display:none, and aria-hidden
        plus tabindex="-1" so no screen-reader or keyboard user is ever asked to
        fill in a field that would get them refused.

        autocomplete="off" matters as much as the hiding: a password manager
        filling in a box labelled "Website" would lock a real player out of
        registering entirely.
      -->
      <div class="hp-field" aria-hidden="true">
        <label for="signup-website">Leave this field empty</label>
        <input id="signup-website" name="website" type="text" value="" tabindex="-1" autocomplete="off" />
      </div>
    `);

    // Signup and reset are where a password is CHOSEN. paintLogin and the
    // "current password" field get nothing: the password already exists there.
    attachPasswordHelper(el);

    el.addEventListener("submit", (e) => {
      e.preventDefault();
      const data = new FormData(el);
      const email = String(data.get("email") ?? "").trim();
      const password = String(data.get("password") ?? "");
      const honeypot = String(data.get("website") ?? "");
      // Advisory only, and deliberately thinner than the server: PasswordPolicy
      // also refuses common passwords and ones derived from the email address.
      // A password this lets through and the server refuses comes back with the
      // server's own message, which is the correct direction to disagree in.
      const weak = window.DrsAuth.describePasswordProblem(password);
      if (weak) {
        show(error, weak);
        return;
      }

      /*
       * No token yet -- the fetch has not landed, or failed. Ask for one and
       * have the player press again rather than posting without it. Deliberately
       * NOT fetch-then-submit: that token would be milliseconds old and refused,
       * which reads as a broken signup rather than as "press it again".
       */
      if (!guard) {
        void fetchSignupToken().then((t) => { guard = t; });
        show(error, "One moment - press Create Account again.");
        return;
      }

      const done = busy(button, "Creating...");
      error.style.display = "none";
      note.style.display = "none";

      signup(email, password, guard, honeypot).then((result) => {
        if (!result.success) {
          /*
           * The guard refused it, so this token is spent whatever the cause --
           * an expired one from a form left open, or a real bot. Drop it and
           * fetch another, which the player uses by pressing the button again.
           */
          if (result.formRefused) {
            guard = null;
            void fetchSignupToken().then((t) => { guard = t; });
          }
          show(error, result.error ?? "Could not create the account.");
          done();
          return;
        }
        // Deliberately says nothing about whether the address was already
        // registered -- the server does not tell us, by design, and inventing
        // a distinction here would hand back the enumeration oracle the
        // server's uniform response exists to prevent.
        el.remove();
        const ok = document.createElement("p");
        ok.textContent = "Check your email for a link to confirm your address, then sign in.";
        wrap.appendChild(ok);
        footer(link("Back to sign in", "login"));
      });
    });

    googleButton();
    footer(link("Back to sign in", "login"));
  }

  // -------------------------------------------------------------------------
  // Forgot password
  // -------------------------------------------------------------------------

  function paintForgot(): void {
    const sub = document.createElement("p");
    sub.className = "dim";
    sub.textContent = "Enter your email and we'll send a link to choose a new password.";
    wrap.appendChild(sub);

    const { el, error, button } = form(`
      <label>
        Email
        <input type="email" name="email" autocomplete="email" required />
      </label>
      <button type="submit">Send Reset Link</button>
    `);

    el.addEventListener("submit", (e) => {
      e.preventDefault();
      const email = String(new FormData(el).get("email") ?? "").trim();
      const done = busy(button, "Sending...");
      error.style.display = "none";

      requestPasswordReset(email).then((result) => {
        if (!result.success) {
          show(error, result.error ?? "Could not send the link.");
          done();
          return;
        }
        el.remove();
        const ok = document.createElement("p");
        // Matches the server's wording, which is deliberately non-committal
        // about whether the address is registered.
        ok.textContent = "If that address has an account, a reset link is on its way.";
        wrap.appendChild(ok);
        footer(link("Back to sign in", "login"));
      });
    });

    footer(link("Back to sign in", "login"));
  }

  // -------------------------------------------------------------------------
  // Redeeming a verification link
  // -------------------------------------------------------------------------

  function paintVerifying(): void {
    const status = document.createElement("p");
    status.textContent = "Confirming your email address...";
    wrap.appendChild(status);

    verifyEmail(verifyToken ?? "").then((result) => {
      clearTokenFromUrl();
      if (result.success) {
        // verify-email.ashx establishes the session itself, so the player is
        // already signed in -- adopting it drops them straight into the game
        // rather than asking for a password they just chose at signup.
        status.textContent = "Email confirmed. Loading your game...";
        adoptSession(result.username ?? "").then(() => ctx.rerender());
        return;
      }
      status.textContent = result.error ?? "That confirmation link is not valid.";
      footer(
        link(result.expired ? "Get a new link" : "Forgot password", "forgot"),
        link("Back to sign in", "login")
      );
    });
  }

  // -------------------------------------------------------------------------
  // Redeeming a reset link
  // -------------------------------------------------------------------------

  function paintReset(): void {
    const sub = document.createElement("p");
    sub.className = "dim";
    sub.textContent = "Choose a new password.";
    wrap.appendChild(sub);

    const { el, error, button } = form(`
      <label>
        New password
        <input type="password" name="password" autocomplete="new-password" minlength="10" required />
      </label>
      <button type="submit">Set Password</button>
    `);

    // Signup and reset are where a password is CHOSEN. paintLogin and the
    // "current password" field get nothing: the password already exists there.
    attachPasswordHelper(el);

    el.addEventListener("submit", (e) => {
      e.preventDefault();
      const password = String(new FormData(el).get("password") ?? "");
      // Advisory only, and deliberately thinner than the server: PasswordPolicy
      // also refuses common passwords and ones derived from the email address.
      // A password this lets through and the server refuses comes back with the
      // server's own message, which is the correct direction to disagree in.
      const weak = window.DrsAuth.describePasswordProblem(password);
      if (weak) {
        show(error, weak);
        return;
      }

      const done = busy(button, "Saving...");
      error.style.display = "none";

      resetPassword(resetToken ?? "", password).then((result: AuthActionResult) => {
        if (result.success) {
          // The token is spent, so clear it only now -- a too-short password
          // is rejected before the token is looked at, and clearing earlier
          // would strand a player who mistyped on their first attempt.
          clearTokenFromUrl();
          adoptSession(result.username ?? "").then(() => ctx.rerender());
          return;
        }
        show(error, result.error ?? "Could not set the password.");
        if (result.expired) {
          clearTokenFromUrl();
          el.remove();
          footer(link("Request a new link", "forgot"));
          return;
        }
        done();
      });
    });
  }
}
